import React from 'react';
import './Auth.css';

/* ── Floating particles ── */
function Particles() {
  const particles = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    left: `${(i * 5.5 + 3) % 100}%`,
    size: `${(i % 4) + 2}px`,
    duration: `${(i % 10) + 8}s`,
    delay: `${(i % 10)}s`,
  }));
  return (
    <div className="auth-particles" aria-hidden>
      {particles.map((p) => (
        <span
          key={p.id}
          className="auth-particle"
          style={{
            left: p.left,
            width: p.size,
            height: p.size,
            animationDuration: p.duration,
            animationDelay: p.delay,
          }}
        />
      ))}
    </div>
  );
}

function IconSun() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}
function IconMoon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  );
}
function IconArrow() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}
function IconUserPlus() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}
function IconLogin() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </svg>
  );
}

/**
 * AuthPage — Landing screen with Login + Sign Up buttons.
 * The robot is now rendered once at the App root level (persistent).
 * Props:
 *   onLogin    : () => void  — navigate to LoginForm
 *   onSignUp   : () => void  — navigate to SignUpForm
 *   isDarkMode : boolean
 *   onToggleTheme : () => void
 */
export function AuthPage({ onLogin, onSignUp, isDarkMode, onToggleTheme }) {
  return (
    <div className="auth-root">
      <Particles />

      {/* Theme toggle */}
      <button
        type="button"
        className="auth-theme-btn"
        onClick={onToggleTheme}
        aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        title="Toggle theme"
      >
        {isDarkMode ? <IconSun /> : <IconMoon />}
      </button>

      {/* ── Left — CTA ── */}
      <div className="auth-left">
        {/* Brand */}
        <div className="auth-brand">
          <div className="auth-brand-badge">
            <span className="auth-brand-dot" />
            AI Powered · Career Guidance
          </div>
          <h1 className="auth-brand-title">Your Career<br />Journey Starts Here</h1>
          <p className="auth-brand-sub">
            Sign in to get personalized career advice powered by AI —
            tailored to your background, goals, and experience.
          </p>
        </div>

        {/* Buttons */}
        <div className="auth-landing-btns">
          <button
            id="auth-login-btn"
            type="button"
            className="auth-btn-primary"
            onClick={onLogin}
          >
            <IconLogin />
            Login
            <IconArrow />
          </button>
          <button
            id="auth-signup-btn"
            type="button"
            className="auth-btn-secondary"
            onClick={onSignUp}
          >
            <IconUserPlus />
            Sign Up
          </button>
        </div>
      </div>

      {/* ── Right — Greeting overlay (robot is behind, rendered by App root) ── */}
      <div className="auth-right">
        <div className="auth-greeting" role="complementary" aria-label="AI Greeting">
          <p className="auth-greeting-title">Hello! I'm your AI Career Counselor 👋</p>
          <p className="auth-greeting-sub">
            Create an account or log in to start your personalized career guidance journey.
          </p>
        </div>
      </div>
    </div>
  );
}
